import CircleIcon from "@mui/icons-material/Circle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
// import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import dayjs from "dayjs";
import { useState } from "react";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import ErrorHelper from "../../../../../components/ErrorWrapper";
import Flex from "../../../../../components/Flex";
import Input from "../../../../../components/Input";
import Text from "../../../../../components/Text";
// import { VisuallyHiddenInput } from "../../../../../components/VisuallyHiddenInput";
import { useSearchParams } from "react-router-dom";
import SignatureInput from "../../../../../components/signiture/SignatureInput";
import { useSignatureController } from "../../../../../components/signiture/use-signature-controller";
import { PAYMENT_POLICY_SECTIONS } from "./new-intake-form-contant/intake-constant";

const bulletListSx = {
  "& .MuiListItem-root": {
    alignItems: "flex-start",
    gap: 1,
    py: 0,
  },
  "& .MuiListItemIcon-root": {
    minWidth: "auto",
    mt: 1,
    "& .MuiSvgIcon-root": {
      fontSize: "8px",
    },
  },
};

const BulletList = ({ items }: { items: string[] }) => (
  <List sx={bulletListSx}>
    {items.map((item, index) => (
      <ListItem key={index}>
        <ListItemIcon>
          <CircleIcon />
        </ListItemIcon>
        <ListItemText
          primary={
            <Text variant="body2" color="textPrimary">
              {item}
            </Text>
          }
        />
      </ListItem>
    ))}
  </List>
);

interface IntakePaymentPoliciesProps {
  values: any;
  setFieldValue: any;
  handleChange: any;
  submitCount: number;
  viewOnly?: boolean;
}

const IntakePaymentPolicies = ({
  values,
  setFieldValue,
  handleChange,
  submitCount,
  viewOnly,
}: IntakePaymentPoliciesProps) => {
  const [k, setK] = useState<boolean>(true);
  const { canvasRef, signPad, isSignEmpty } = useSignatureController(
    k,
    values?.paymentPolicySign
  );
  // const uploadInputRef = useRef<HTMLInputElement | null>(null);

  // const handleUploadSignature = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const file = e.target.files?.[0];
  //   if (!file) return;
  //   const reader = new FileReader();
  //   reader.onload = () => {
  //     const dataUrl = reader.result as string;
  //     signPad?.clear();
  //     signPad?.fromDataURL(dataUrl);
  //     setFieldValue("paymentPolicySign", dataUrl);
  //   };
  //   reader.readAsDataURL(file);
  //   e.target.value = "";
  // };
  const [searchParams] = useSearchParams();
  const patientName = searchParams.get("name");
  const patientDob = dayjs(searchParams.get("dob"));

  return (
    <Box>
      <Text variant="subtitle1" fontWeight={600} mb={2}>
        Payment Policy & Financial Responsibility Consent Form
      </Text>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <Input
            label="Patient Name"
            textFieldProps={{
              name: "paymentPolicyPatientName",
              value: values?.paymentPolicyPatientName ?? patientName,
              onChange: handleChange,
              placeholder: "Enter Patient Name",
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <CustomDatePicker
            disabled={viewOnly}
            label="Date of Birth"
            name="paymentPolicyDob"
            placeholder="Select Date of Birth"
            value={
              values?.paymentPolicyDob
                ? dayjs(values.paymentPolicyDob)
                : dayjs(patientDob)
            }
            onChange={(value) => setFieldValue("paymentPolicyDob", value)}
            maxDate={dayjs()}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <CustomDatePicker
            disabled={viewOnly}
            label="Date"
            name="paymentPolicyDate"
            placeholder="Select Date"
            value={values.paymentPolicyDate}
            onChange={(value) => setFieldValue("paymentPolicyDate", value)}
            maxDate={dayjs()}
          />
        </Grid>
      </Grid>

      <Flex flexDirection="column" gap={2}>
        {PAYMENT_POLICY_SECTIONS.map((section) => (
          <Box key={section.title}>
            <Text variant="subtitle1" fontWeight={600} mb={0.5}>
              {section.title}
            </Text>
            {section.body && (
              <Text variant="body2" color="textPrimary">
                {section.body}
              </Text>
            )}
            {section.bullets && <BulletList items={section.bullets} />}
            {section.footer && <Text>{section.footer}</Text>}
          </Box>
        ))}
      </Flex>

      <Box mt={3}>
        <Text variant="subtitle1" fontWeight={600} mb={1}>
          Acknowledgment & Consent
        </Text>
        <Text variant="body2" color="textPrimary" mb={2}>
          I have read, understood, and agree to the Payment Policy and Financial
          Responsibility terms outlined above. I acknowledge that I am
          responsible for all charges not covered by my insurance.
        </Text>

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6}>
            <Input
              label="Printed Name"
              textFieldProps={{
                name: "paymentPolicyPrintedName",
                value: values?.paymentPolicyPrintedName ?? patientName,
                onChange: handleChange,
                placeholder: "Enter Printed Name",
                disabled: viewOnly,
              }}
            />
          </Grid>
        </Grid>

        <Grid item container>
          {viewOnly || values?.paymentPolicySign ? (
            <Box>
              <ErrorHelper label={"Patient/Responsible Party Signature"}>
                <Box
                  style={{
                    position: "relative",
                    borderWidth: 1,
                    borderRadius: 5,
                    borderStyle: "solid",
                    height: "100%",
                    width: "fit-content",
                  }}
                >
                  <img
                    src={values?.paymentPolicySign}
                    style={{ height: "150px", width: "470px" }}
                  />

                  {!viewOnly && (
                    <IconButton
                      color="error"
                      onClick={() => {
                        setK((prev) => !prev);
                        signPad?.clear();
                        setFieldValue("paymentPolicySign", undefined);
                      }}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -20,
                        height: "10px",
                      }}
                    >
                      <HighlightOffIcon color="error" fontSize="medium" />
                    </IconButton>
                  )}
                </Box>
              </ErrorHelper>
            </Box>
          ) : (
            <Flex flexDirection="column" gap={1}>
              <Box
                position={"relative"}
                onMouseLeave={() => {
                  if (!isSignEmpty() && signPad?.toDataURL()) {
                    setFieldValue("paymentPolicySign", signPad?.toDataURL());
                  }
                }}
              >
                <SignatureInput
                  required
                  error={submitCount > 0 && isSignEmpty()}
                  label="Patient/Responsible Party Signature"
                  canvasRef={canvasRef}
                  w="470px"
                  h="150px"
                />

                <Button
                  variant="contained"
                  sx={{
                    position: "absolute",
                    bottom: 6,
                    right: 10,
                    height: "24px",
                  }}
                  onClick={() => {
                    setK((prev) => !prev);
                    signPad?.clear();
                  }}
                >
                  clear
                </Button>
              </Box>

              {/* <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileOutlinedIcon />}
                onClick={() => uploadInputRef.current?.click()}
                sx={{ alignSelf: "flex-start" }}
              >
                Upload Signature
                <VisuallyHiddenInput
                  ref={uploadInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUploadSignature}
                />
              </Button> */}
            </Flex>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default IntakePaymentPolicies;
